import { PrismicRichText } from '@prismicio/react';
import Fuse from 'fuse.js';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionProps,
  Box,
  Container,
  DarkTheme,
  H1,
  H2,
  H3,
  Input,
  IoSearchSharp,
  Lead,
  P,
  Section,
} from 'react-components';

import { RichContentWrapper } from 'shared/components/rich-content-wrapper';
import { faqsService } from 'shared/service/faqs';
import { Faqs } from 'shared/service/faqs/faqs-service';
import styled from 'styled-components';
import { PaperPlaneSection } from './components/paper-plane-section';

const AnswerBox = styled(Box)`
  &:last-of-type {
    border-bottom-width: 0;
  }
`;

const StyledPrismicRichContentWrapper = styled(RichContentWrapper)`
  p {
    margin-top: 1.5rem;
    font-size: 1.25rem;
  }
`;

const Answer: React.FunctionComponent<{ title: string; answer: any; id: string }> = ({
  title,
  answer,
  id,
}) => {
  return (
    <AnswerBox
      borderBottomStyle="solid"
      borderBottomWidth="1px"
      borderBottomColor="blacks.black200"
      key={id}
    >
      <H3 mt="3rem" mb="1.5rem" fontSize={{ _: '2rem', sm: '3rem' }}>
        {title}
      </H3>
      <StyledPrismicRichContentWrapper>
        <PrismicRichText field={answer} />
      </StyledPrismicRichContentWrapper>
    </AnswerBox>
  );
};

export const HelpPage: React.FunctionComponent = () => {
  const [faqs, setFaqs] = useState<Faqs>([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => setFaqs(await faqsService.loadFaqs()))();
  }, []);

  const fuse = useMemo(() => {
    const questions = (faqs ?? []).flatMap(({ questions }) => questions);

    return new Fuse(questions, {
      includeScore: true,
      shouldSort: true,
      keys: [
        {
          name: 'title',
          weight: 2,
        },
        {
          name: 'answer.text',
          weight: 1,
        },
      ],
    });
  }, [faqs]);

  const searchResults = useMemo(() => fuse.search(search), [fuse, search]);

  const items: AccordionProps['items'] = (faqs || []).map((category) => ({
    key: category.id,
    title: category.title,
    content: (
      <Box>
        {category.questions.map(({ id, title, answer }) => (
          <Answer key={id} id={id} title={title} answer={answer} />
        ))}
      </Box>
    ),
  }));

  return (
    <>
      <Section bg="black">
        <Container pt="9rem" pb="3rem">
          <H1 textAlign={{ _: 'start', sm: 'center' }} color="white" mb={4} mt={0}>
            Learn
          </H1>
          <Lead
            color="blacks.black200"
            maxWidth="51rem"
            mx="auto"
            textAlign={{ _: 'start', sm: 'center' }}
          >
            Crypto is new to many of us. We feel it&apos;s important that you can learn about the
            basic concepts and benefits in simple terms.
          </Lead>
          <Box mx="auto" maxWidth="40rem" mt="3.25rem">
            <DarkTheme>
              <Input
                id="search"
                label="What are you looking for?"
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                inputAction={{
                  icon: IoSearchSharp,
                  onClick: () => {},
                }}
              />
            </DarkTheme>
          </Box>
        </Container>
      </Section>
      {!!search && (
        <Section>
          <Container>
            <H2>Search results</H2>
            {[...searchResults]
              .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
              .map(({ item: { answer, title, id }, score }) => (
                <React.Fragment key={id}>
                  {answer && <Answer id={id} title={title} answer={answer} />}
                </React.Fragment>
              ))}
            {searchResults.length === 0 && <P>No results found</P>}
          </Container>
        </Section>
      )}
      <Section>
        <Container my={{ _: '3rem', sm: '6rem' }} $noPadding={true}>
          <Box mx="auto" maxWidth="51rem">
            <Accordion items={items} />
          </Box>
        </Container>
      </Section>
      <PaperPlaneSection title="Didn't find what you're looking for?" lead="Reach out to us!" />
    </>
  );
};
